// Utility functions for formatting dates and times in IST (Indian Standard Time)

/**
 * Format date/time to IST with custom options
 * @param {string|Date} dateString - Date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date/time string in IST
 */
export const formatToIST = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    ...options
  });
};

/**
 * Format date in IST (e.g., "15 Jan 2024")
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateIST = (dateString) => {
  return formatToIST(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format time in IST (e.g., "02:30 PM")
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted time string
 */
export const formatTimeIST = (dateString) => {
  return formatToIST(dateString, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format time without timezone conversion (e.g., "02:30 PM")
 * Uses local time as stored in the database
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted time string
 */
export const formatTimeLocal = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format date and time in IST (e.g., "15 Jan 2024, 02:30 PM")
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date and time string
 */
export const formatDateTimeIST = (dateString) => {
  return formatToIST(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format date in full format (e.g., "Monday, 15 January 2024")
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateFullIST = (dateString) => {
  return formatToIST(dateString, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format date with time in full format (e.g., "Monday, 15 January 2024 at 02:30 PM")
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date and time string
 */
export const formatDateTimeFullIST = (dateString) => {
  return formatToIST(dateString, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};
